import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import apiClient from "./api/client";
import LoanGrid from "./components/LoanGrid";
import LoanForm from "./components/LoanForm.jsx";
import LoginPage from "./components/LoginPage.jsx";
import AccountPage from "./components/AccountPage.jsx";
import UserManagementPage from "./components/UserManagementPage.jsx";
import CompanyManagementPage from "./components/CompanyManagementPage.jsx";
import ActivityLogPage from "./components/ActivityLogPage.jsx";
import UserProfilePage from "./components/UserProfilePage.jsx";
import Dashboard from "./components/Dashboard.jsx";
import LoanDetailPage from "./components/LoanDetailPage.jsx";
import AssetDetailPage from "./components/AssetDetailPage.jsx";
import CollateralPage from "./components/CollateralPage.jsx";
import CashFlowPage from "./components/CashFlowPage.jsx";
import CreditFacilitiesPage from "./components/CreditFacilitiesPage.jsx";
import BalanceSheetPage from "./components/BalanceSheetPage.jsx";
import IncomeStatementPage from "./components/IncomeStatementPage.jsx";
import FinancialStatementsTabs from "./components/FinancialStatementsTabs.jsx";
import ReportsPage from "./components/ReportsPage.jsx";
import DailyReportPage from "./components/DailyReportPage.jsx";
import FeedbackButton from "./components/FeedbackButton.jsx";
import StockWidget from "./components/StockWidget.jsx";
import GlobalSearch from "./components/GlobalSearch.jsx";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { can } from "./utils/permissions";
import "./App.css";
import "./themes/theme-a.css";
import "./themes/theme-b.css";
import "./themes/theme-c.css";

// ── Sidebar group item with hover flyout ──────────────────────────────────────

function NavGroupItem({ group, sidebarCollapsed, setSidebarOpen }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isChildActive = group.children.some(
    (c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/")
  );

  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Parent row */}
      <NavLink
        to={group.to}
        className={({ isActive }) =>
          "sidebar-link" + (isChildActive && !isActive ? " active" : "")
        }
        onClick={() => setSidebarOpen(false)}
        title={sidebarCollapsed ? group.label : undefined}
      >
        <span className="sidebar-link-icon">{group.icon}</span>
        <span className="sidebar-link-text">{group.label}</span>
        {!sidebarCollapsed && (
          <span style={{
            fontSize: 9, opacity: 0.4,
            display: "inline-block",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.56s ease",
          }}>▾</span>
        )}
      </NavLink>

      {/* Inline children — always mounted, animated via max-height */}
      <div style={{
        maxHeight: open ? 200 : 0,
        overflow: "hidden",
        transition: "max-height 0.56s ease",
        borderRight: open ? "2px solid var(--border-light)" : "2px solid transparent",
        marginRight: 14,
        marginBottom: open ? 2 : 0,
      }}>
        {group.children.map((child) => (
          <NavLink
            key={child.to}
            to={child.to}
            className="sidebar-link"
            onClick={() => { setSidebarOpen(false); setOpen(false); }}
            style={{ fontSize: 13, padding: "9px 12px" }}
          >
            <span className="sidebar-link-icon" style={{ fontSize: 12 }}>{child.icon}</span>
            <span className="sidebar-link-text">{child.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function AppInner() {
  const { theme } = useTheme();
  const [auth, setAuth] = useState({ user: null, token: null });
  const [authLoaded, setAuthLoaded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);       // mobile drawer
  const [sidebarCollapsed, setSidebarCollapsed] = useState(   // desktop icon-only
    () => localStorage.getItem("mashva-sidebar-collapsed") === "true"
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem("auth");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.user) {
          setAuth({ user: parsed.user, token: parsed.token || null });
          // Cookie is restored automatically by the browser; no header needed
        }
      } catch (e) {
        console.error("Failed to parse saved auth", e);
      }
    }
    setAuthLoaded(true);
  }, []);

  // Poll pending-count for sidebar badge (admin only, every 60s)
  useEffect(() => {
    const currentUser = auth.user;
    if (!currentUser || currentUser.role !== "ADMIN") { setPendingCount(0); return; }
    const poll = () => {
      apiClient.get("/auth/pending-count")
        .then((res) => setPendingCount(res.data.pendingUsers || 0))
        .catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 60_000);
    return () => clearInterval(interval);
  }, [auth.user]);

  // CMD+K / Ctrl+K → open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleLogin = (user, token) => {
    const next = { user, token };  // keep token in state for any legacy use
    setAuth(next);
    localStorage.setItem("auth", JSON.stringify({ user })); // don't persist token
  };

  const handleLogout = () => {
    apiClient.post("/auth/logout").catch(() => {});
    setAuth({ user: null, token: null });
    localStorage.removeItem("auth");
    delete axios.defaults.headers.common["Authorization"];
  };

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      localStorage.setItem("mashva-sidebar-collapsed", String(next));
      return next;
    });
  };

  const currentUser = auth.user;
  const isAdmin = Boolean(currentUser && currentUser.role === "ADMIN");

  // Route-level access using the new granular can() utility.
  // can() automatically handles both old flat schema and new nested schema.
  const canViewLoans        = can(currentUser, "routes.loans");
  const canViewCollateral   = can(currentUser, "routes.collateral");
  const canViewCreditFac    = can(currentUser, "routes.creditFacilities");
  const canViewBalanceSheet = can(currentUser, "routes.balanceSheet");
  const canViewIncomeSt     = can(currentUser, "routes.incomeStatement");
  const canViewCashFlow     = can(currentUser, "routes.cashFlow");
  const canViewFinancials   = can(currentUser, "routes.financials");
  const canViewReports      = can(currentUser, "routes.reports");
  const canCreateLoans      = can(currentUser, "loans.create");
  const canEditLoans        = can(currentUser, "loans.edit");

  // Build sidebar nav items — each entry only shown when user has the matching route permission
  const loansChildren = [
    { to: "/loans",             label: "טבלת הלוואות",     icon: "≡",  show: canViewLoans },
    { to: "/collateral",        label: "בטחונות ושעבודים", icon: "🔒", show: canViewCollateral },
    { to: "/credit-facilities", label: "מסגרות אשראי",     icon: "▤",  show: canViewCreditFac },
  ].filter((c) => c.show);

  const financialsChildren = [
    { to: "/balance-sheet",    label: "מאזן",             icon: "⊞", show: canViewBalanceSheet },
    { to: "/income-statement", label: "דוח רווח והפסד",   icon: "₪", show: canViewIncomeSt },
    { to: "/cashflow",         label: "תזרים מזומנים",    icon: "↕", show: canViewCashFlow },
  ].filter((c) => c.show);

  const navItems = [
    { type: "link", to: "/dashboard", label: "לוח בקרה", icon: "⊡" },
    ...(loansChildren.length > 0 ? [{
      type: "group", to: "/loans", label: "הלוואות", icon: "≡",
      children: loansChildren,
    }] : []),
    ...(canViewReports ? [{ type: "link", to: "/reports", label: "דוחות", icon: "📋" }] : []),
    ...(financialsChildren.length > 0 ? [{
      type: "group", to: "/financials", label: "דוחות פיננסיים", icon: "📊",
      children: financialsChildren,
    }] : []),
    ...(isAdmin ? [{ type: "link", to: "/user-management", label: "ניהול משתמשים", icon: "👥", pendingCount: pendingCount }] : []),
    ...(isAdmin ? [{ type: "link", to: "/company-management", label: "ניהול חברות", icon: "🏢" }] : []),
  ];

  return (
    <div className={`app theme-${theme}`}>

      {/* ── Header ──────────────────────────────────────────── */}
      <header className="app-header">
        <div className="app-header-inner">
          {currentUser && (
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="תפריט"
            >
              <span className="sidebar-toggle-icon">☰</span>
            </button>
          )}

          {/* Brand */}
          <div className="app-brand" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: "var(--accent)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 800, letterSpacing: "-0.02em", flexShrink: 0,
            }}>
              מ
            </div>
            <span>משב״ה</span>
          </div>

          {/* CMD+K search trigger (desktop) */}
          {currentUser && (
            <button
              type="button"
              className="header-search-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="חיפוש"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>חיפוש...</span>
              <kbd>⌘K</kbd>
            </button>
          )}

          {/* User area */}
          <div className="app-user">
            {currentUser && (
              <>
                <span className="app-user-name">
                  {currentUser.firstName} {currentUser.lastName}
                </span>
                <Link to="/account" className="btn btn-ghost btn-header">החשבון שלי</Link>
                <button type="button" className="btn btn-ghost btn-header" onClick={handleLogout}>
                  התנתק
                </button>
              </>
            )}
            {!currentUser && (
              <Link to="/login" className="btn btn-primary">התחברות</Link>
            )}
          </div>
        </div>
      </header>

      {/* ── Body: sidebar + main ─────────────────────────────── */}
      <div className="app-body">
        {currentUser && (
          <>
            {sidebarOpen && (
              <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
            )}
            <aside
              className={`app-sidebar ${sidebarOpen ? "sidebar-open" : ""} ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}
              style={{ display: "flex", flexDirection: "column" }}
            >
              {/* Collapse toggle (desktop only) */}
              <button
                type="button"
                className="sidebar-collapse-btn"
                onClick={toggleSidebarCollapsed}
                aria-label={sidebarCollapsed ? "הרחב סרגל" : "כווץ סרגל"}
                title={sidebarCollapsed ? "הרחב סרגל" : "כווץ סרגל"}
              >
                <span className="sidebar-collapse-icon">{sidebarCollapsed ? "›" : "‹"}</span>
              </button>

              <nav className="sidebar-nav" style={{ flex: 1 }}>
                {navItems.map((item) => {
                  if (item.type === "group") {
                    return (
                      <NavGroupItem
                        key={item.to}
                        group={item}
                        sidebarCollapsed={sidebarCollapsed}
                        setSidebarOpen={setSidebarOpen}
                      />
                    );
                  }
                  // flat link
                  const { to, label, icon, pendingCount: pc } = item;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      className="sidebar-link"
                      onClick={() => setSidebarOpen(false)}
                      title={sidebarCollapsed ? label : undefined}
                    >
                      <span className="sidebar-link-icon">{icon}</span>
                      <span className="sidebar-link-text">{label}</span>
                      {pc > 0 && (
                        <span
                          title={`${pc} בקשות הרשמה ממתינות לאישור`}
                          style={{
                            width: 18, height: 18,
                            borderRadius: "50%",
                            background: "#ef4444",
                            color: "#fff",
                            fontSize: 10, fontWeight: 700,
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                            boxShadow: "0 1px 4px rgba(239,68,68,0.4)",
                          }}
                        >
                          {pc}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </nav>

              {/* Theme switcher hidden — design locked to Theme A */}
              {/* <ThemeSwitcher collapsed={sidebarCollapsed} /> */}
            </aside>
          </>
        )}

        <main className="app-main">
          {!authLoaded ? (
            <div className="app-loading">טוען...</div>
          ) : (
            <Routes>
              <Route path="/" element={currentUser ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} />
              <Route path="/dashboard" element={currentUser ? <Dashboard /> : <Navigate to="/login" replace />} />

              {/* Loans */}
              <Route path="/loans" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewLoans ? <LoanGrid onUnauthorized={handleLogout} canEdit={canEditLoans} />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/create" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canCreateLoans ? <LoanForm />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/edit/:id" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canEditLoans ? <LoanForm />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/loan/:id" element={currentUser ? <LoanDetailPage /> : <Navigate to="/login" replace />} />
              <Route path="/asset/:assetType/:id" element={currentUser ? <AssetDetailPage /> : <Navigate to="/login" replace />} />

              {/* Account */}
              <Route path="/account" element={currentUser ? <AccountPage user={currentUser} /> : <Navigate to="/login" replace />} />

              {/* Admin-only routes */}
              <Route path="/user-management" element={
                isAdmin ? <UserManagementPage user={currentUser} />
                  : currentUser ? <Navigate to="/" replace />
                  : <Navigate to="/login" replace />
              } />
              <Route path="/company-management" element={
                isAdmin ? <CompanyManagementPage />
                  : currentUser ? <Navigate to="/" replace />
                  : <Navigate to="/login" replace />
              } />
              <Route path="/activity-log" element={
                isAdmin ? <ActivityLogPage />
                  : currentUser ? <Navigate to="/" replace />
                  : <Navigate to="/login" replace />
              } />
              <Route path="/users/:id" element={
                isAdmin ? <UserProfilePage currentUser={currentUser} />
                  : currentUser ? <Navigate to="/" replace />
                  : <Navigate to="/login" replace />
              } />

              {/* Collateral */}
              <Route path="/collateral" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewCollateral ? <CollateralPage onUnauthorized={handleLogout} canEdit={canEditLoans} />
                  : <Navigate to="/dashboard" replace />
              } />

              {/* Financial statements */}
              <Route path="/balance-sheet" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewBalanceSheet ? <BalanceSheetPage onUnauthorized={handleLogout} canEdit={canEditLoans} />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/income-statement" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewIncomeSt ? <IncomeStatementPage onUnauthorized={handleLogout} />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/cashflow" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewCashFlow ? <CashFlowPage onUnauthorized={handleLogout} canEdit={canEditLoans} />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/credit-facilities" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewCreditFac ? <CreditFacilitiesPage onUnauthorized={handleLogout} canEdit={canEditLoans} />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/financials" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewFinancials ? <FinancialStatementsTabs />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/reports" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewReports ? <ReportsPage />
                  : <Navigate to="/dashboard" replace />
              } />
              <Route path="/reports/daily" element={
                !currentUser ? <Navigate to="/login" replace />
                  : canViewReports ? <DailyReportPage />
                  : <Navigate to="/dashboard" replace />
              } />

              <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
              <Route path="*" element={
                currentUser ? (
                  <div style={{ padding: "60px 40px", textAlign: "center" }}>
                    <h2 style={{ color: "var(--text-primary)", marginBottom: 8 }}>404 — הדף לא נמצא</h2>
                    <p style={{ color: "var(--text-secondary)" }}>הדף שחיפשת אינו קיים.</p>
                    <a href="/dashboard" style={{ color: "var(--accent)" }}>חזרה לדשבורד</a>
                  </div>
                ) : <Navigate to="/login" replace />
              } />
            </Routes>
          )}
        </main>
      </div>

      {/* ── Global search overlay ────────────────────────────── */}
      {currentUser && (
        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      )}

      {/* ── Stock widget ─────────────────────────────────────── */}
      {currentUser && <StockWidget />}

      {/* ── Feedback ─────────────────────────────────────────── */}
      {currentUser && <FeedbackButton />}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </ThemeProvider>
  );
}
